import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { TwcTreeItemCheckboxState } from '@topcon/twc-angular';

import { PcvizCRP1FileData, PcvizCRP1Project, PcvizCRP1ScanData } from '@mirage-projects/cw-models';

/**
 * Scan selection tree for the import crp1 stations workflow
 */
@Component({
    selector: 'cw-scan-selection-tree',
    templateUrl: 'scan-selection-tree.component.html',
    styleUrls: ['scan-selection-tree.component.scss'],
    standalone: false,
})
export class ScanSelectionTreeComponent implements OnInit, OnChanges {
    /** CRPQ SDCard file data */
    @Input() fileData: PcvizCRP1FileData | undefined;
    /** Data with the selected scans list */
    @Output() selectionChange = new EventEmitter<PcvizCRP1ScanData[]>();

    projects: PcvizCRP1Project[] = [];
    searchText = '';

    private scansSelection!: Map<PcvizCRP1ScanData, TwcTreeItemCheckboxState>;
    private projectsExpandedState!: Map<PcvizCRP1Project, boolean>;

    ngOnInit(): void {
        this.updateProjectData(this.fileData);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.['fileData']?.currentValue) {
            this.updateProjectData(changes?.['fileData']?.currentValue);
        }
    }

    /**
     * On checked elements change actions
     */
    onCheckedChange(scan: PcvizCRP1ScanData, state: TwcTreeItemCheckboxState): void {
        this.scansSelection.set(scan, state);
        this.emitSelectedScans();
    }

    /**
     * Gets checked value for a given scan
     * @param scan Scan to check
     */
    isChecked(scan: PcvizCRP1ScanData): TwcTreeItemCheckboxState {
        return this.scansSelection.get(scan) ?? 'unselected';
    }

    /**
     * Updates project expanded state
     * @param project Project to update state
     * @param state State value
     */
    onExpandedChange(project: PcvizCRP1Project, state: boolean): void {
        this.projectsExpandedState.set(project, state);
    }

    /**
     * Geets expanded value for project
     * @param project Project to get expanded value from
     */
    isExpanded(project: PcvizCRP1Project): boolean {
        const isProjectInSearch = !!this.searchText && this.isProjectVisible(project);
        return isProjectInSearch || !!this.projectsExpandedState.get(project);
    }

    /**
     * Removes current search
     */
    clearSearch(): void {
        this.searchText = '';
    }

    /**
     * Gets the visble scans of the list filtered by search field.
     * If a project is given and project name contains the search string, the scans will be visible
     * @param scans scans to filter
     * @param project optional project to check search against the project name
     */
    getVisibleScans(scans: PcvizCRP1ScanData[], project: PcvizCRP1Project): PcvizCRP1ScanData[] {
        const isTextInProject = project ? project.name.toLowerCase().includes(this.searchText.toLowerCase()) : false;
        return scans.filter((scan) => isTextInProject || scan.name.toLowerCase().includes(this.searchText.toLowerCase()));
    }

    /**
     * Gets the visble projects of the list filtered by search field.
     * @param projects projects to filter
     */
    getVisibleProjects(projects: PcvizCRP1Project[]): PcvizCRP1Project[] {
        return projects.filter((project) => this.isProjectVisible(project));
    }

    /**
     * Checks if a project is visible
     * @param project Project to check
     */
    isProjectVisible(project: PcvizCRP1Project): boolean {
        if (!this.searchText) {
            return true;
        }

        return !!this.getVisibleScans(project.scans, project).length;
    }

    private updateProjectData(data: PcvizCRP1FileData | undefined): void {
        if (data) {
            this.projects = this.getProjects(this.fileData);
            const projectExpandedEntries: [PcvizCRP1Project, boolean][] = this.projects.map((project) => [project, true]);
            this.projectsExpandedState = new Map(projectExpandedEntries);
            const scans = this.projects.flatMap((project) => project.scans);
            const scanSelectionEntries: [PcvizCRP1ScanData, TwcTreeItemCheckboxState][] = scans.map((scan) => [scan, 'unselected']);
            this.scansSelection = new Map(scanSelectionEntries);
        } else {
            this.projects = [];
            this.scansSelection = new Map();
        }
    }

    private emitSelectedScans(): void {
        const selectedScanEntries = [...this.scansSelection.entries()].filter(([, state]) => state === 'selected');
        const selectedScans = selectedScanEntries.map(([scan]) => scan);
        this.selectionChange.emit(selectedScans);
    }

    private getProjects(fileData: PcvizCRP1FileData | undefined): PcvizCRP1Project[] {
        return fileData?.content?.projects ?? [];
    }
}
